import { useState, useRef, useEffect } from 'react';
import Countdown from '../components/Countdown.jsx';
import InfoCard from '../components/InfoCard.jsx';
import { get, set } from '../lib/storage.js';
import './Home.css';

function pageIcon(slug) {
  return <img src={`/assets/eagle/page-icons/${slug}.png`} width="32" height="32" alt="" />;
}

const TILES = [
  {
    path: '/day-in-the-life',
    icon: pageIcon('day'),
    label: 'Day in the Life',
    desc: 'Hours, lunch, phones, lockers, and what to do if something goes wrong',
  },
  {
    path: '/courses',
    icon: pageIcon('courses'),
    label: 'Courses',
    desc: 'Full 6th–8th grade catalog, filterable by grade and subject',
  },
  {
    path: '/schedule-builder',
    icon: pageIcon('schedule'),
    label: 'Schedule Builder',
    desc: 'Build and save your course schedule with live validation',
  },
  {
    path: '/clubs',
    icon: pageIcon('clubs'),
    label: 'Clubs',
    desc: '25+ clubs with meeting times, categories, and favorites',
  },
  {
    path: '/sports-pe',
    icon: pageIcon('sports'),
    label: 'Sports & PE',
    desc: '6th grade PE options and what UIL sports unlock in 7th',
  },
  {
    path: '/events',
    icon: pageIcon('events'),
    label: 'Events',
    desc: 'Dances, pep rallies, spirit weeks, and school traditions',
  },
  {
    path: '/rules',
    icon: pageIcon('rules'),
    label: 'Rules',
    desc: 'Dress code, phone rules, academic integrity, and what happens if...',
  },
  {
    path: '/timeline',
    icon: pageIcon('timeline'),
    label: 'Timeline',
    desc: 'Every deadline and milestone from now through May 2027',
  },
  {
    path: '/faq',
    icon: pageIcon('faq'),
    label: 'FAQ',
    desc: 'Answers for every new 6th grader — especially from Montessori',
  },
  {
    path: '/people',
    icon: pageIcon('people'),
    label: 'People',
    desc: 'Staff directory with email links and who to contact for what',
  },
  {
    path: '/notes',
    icon: pageIcon('notes'),
    label: 'Notes',
    desc: 'Questions, worries, wins — your private journal, never leaves this device',
  },
];

export default function Home() {
  const [name, setName]       = useState(() => get('crms.greeting', ''));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const inputRef              = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function startEditing() {
    setDraft(name);
    setEditing(true);
  }

  function commitName() {
    const trimmed = draft.trim();
    setName(trimmed);
    set('crms.greeting', trimmed);
    setEditing(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter')  commitName();
    if (e.key === 'Escape') setEditing(false);
  }

  return (
    <div className="home">

      <section className="home-hero" aria-label="Welcome">
        <div className="home-greeting">
          {editing ? (
            <div className="home-name-edit">
              <label className="sr-only" htmlFor="name-input">
                Your name (shown in greeting)
              </label>
              <input
                ref={inputRef}
                id="name-input"
                className="home-name-input"
                type="text"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={commitName}
                placeholder="Your name"
                maxLength={40}
              />
              <button
                type="button"
                className="btn btn-primary"
                onMouseDown={e => {
                  // Prevent onBlur from firing before onClick
                  e.preventDefault();
                }}
                onClick={commitName}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onMouseDown={e => e.preventDefault()}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="home-greeting-display">
              <h1 className="home-greeting-text">
                Hey, {name || 'Eagle'}!
              </h1>
              <button
                type="button"
                className="home-edit-btn"
                onClick={startEditing}
              >
                {name ? 'edit name' : 'add your name'}
              </button>
            </div>
          )}
        </div>

        <img
          src="./assets/eagle/hero.png"
          alt="Eagle in flight"
          width="800"
          height="498"
          loading="eager"
          className="home-hero-img"
        />

        <Countdown />
      </section>

      <section className="home-tiles" aria-labelledby="tiles-heading">
        <h2 id="tiles-heading" className="sr-only">Your guide sections</h2>
        <div className="home-tiles-grid">
          {TILES.map(({ path, icon, label, desc }) => (
            <InfoCard
              key={path}
              to={path}
              icon={icon}
              title={label}
              titleAs="p"
              description={desc}
              variant="tile"
            />
          ))}
        </div>
      </section>

    </div>
  );
}
